import { create } from 'zustand';
import type { TranscriptSource } from '@stream-buddy/shared';

// Transcript entry for activity log
export interface TranscriptEntry {
  id: string;
  text: string;
  thought?: string; // AI's internal reasoning (displayed dimmer)
  timestamp: number;
  source?: TranscriptSource;
}

export type BackgroundStyle = 'stars' | 'cosmic' | 'nebula';

export interface GameStat {
  value: number;
  label?: string;
  updatedAt: number;
  lastUpdateMetadata?: string;
}

interface SessionState {
  // Connection state
  isConnected: boolean;
  isStopping: boolean;
  isReconnecting: boolean;
  reconnectFailed: boolean;
  isChatConnected: boolean;
  isChatAuthenticated: boolean; // true if chat is authenticated (not anonymous)
  isGeminiConnected: boolean;
  isOverlayConnected: boolean;
  lastStatusReceivedAt: number; // timestamp of last status message - prevents flash during reconnect

  // Mute state
  isMuted: boolean;
  muteExpiresAt: number | null;

  // AI state
  isAiSpeaking: boolean;
  isAiThinking: boolean;

  // Session health / degradation
  sessionHealth: {
    degraded: boolean;
    severity: 'info' | 'warning' | 'critical';
    reasons: string[];
    sampledAt: number;
  } | null;

  // Activity log transcript
  transcript: TranscriptEntry[];

  // Stats
  messagesAnalyzed: number;
  alertsTriggered: number;
  donationsRead: number;
  gameStats: Record<string, GameStat>;

  // UI Settings
  backgroundStyle: BackgroundStyle;

  // Actions
  setConnected: (connected: boolean) => void;
  setStopping: (stopping: boolean) => void;
  setReconnecting: (reconnecting: boolean) => void;
  setReconnectFailed: (failed: boolean) => void;
  setChatConnected: (connected: boolean, authenticated?: boolean) => void;
  setGeminiConnected: (connected: boolean) => void;
  setOverlayConnected: (connected: boolean) => void;
  setMuted: (muted: boolean, expiresAt?: number | null) => void;
  setAiSpeaking: (speaking: boolean) => void;
  setAiThinking: (thinking: boolean) => void;
  setSessionHealth: (health: SessionState['sessionHealth']) => void;
  addTranscriptEntry: (text: string, thought?: string, source?: TranscriptSource) => void;
  clearTranscript: () => void;
  updateStats: (stats: Partial<Pick<SessionState, 'messagesAnalyzed' | 'alertsTriggered' | 'donationsRead' | 'gameStats'>>) => void;
  setBackgroundStyle: (style: BackgroundStyle) => void;
  reset: () => void;
}

const TRANSCRIPT_DUPLICATE_WINDOW_MS = 1500;
const MAX_TRANSCRIPT_ENTRIES = 250;

function getInitialBackgroundStyle(): BackgroundStyle {
  const stored = localStorage.getItem('sb_bg_style') as BackgroundStyle | null;
  const migrated = localStorage.getItem('sb_bg_nebula_default_migrated') === 'true';

  if (!migrated) {
    localStorage.setItem('sb_bg_nebula_default_migrated', 'true');
    if (stored === 'cosmic' || stored === 'stars') {
      localStorage.setItem('sb_bg_style', 'nebula');
      return 'nebula';
    }
  }

  return stored || 'nebula';
}

const initialState = {
  isConnected: false,
  isStopping: false,
  isReconnecting: false,
  reconnectFailed: false,
  isChatConnected: false,
  isChatAuthenticated: false,
  isGeminiConnected: false,
  isOverlayConnected: false,
  lastStatusReceivedAt: 0,
  isMuted: false,
  muteExpiresAt: null,
  isAiSpeaking: false,
  isAiThinking: false,
  sessionHealth: null,
  transcript: [] as TranscriptEntry[],
  messagesAnalyzed: 0,
  alertsTriggered: 0,
  donationsRead: 0,
  gameStats: {} as Record<string, GameStat>,
  backgroundStyle: getInitialBackgroundStyle(),
};

export const useSessionStore = create<SessionState>((set) => ({
  ...initialState,

  setConnected: (connected) => set({ isConnected: connected }),

  setStopping: (stopping) => set({ isStopping: stopping }),

  setReconnecting: (reconnecting) => set({ isReconnecting: reconnecting, reconnectFailed: false }),

  setReconnectFailed: (failed) => set({ reconnectFailed: failed, isReconnecting: false }),

  setChatConnected: (connected, authenticated = false) => set({
    isChatConnected: connected,
    isChatAuthenticated: connected ? authenticated : false,
    // Track when we last received a status update - used to prevent flash during reconnect
    lastStatusReceivedAt: Date.now(),
  }),

  setGeminiConnected: (connected) => set({ isGeminiConnected: connected }),

  setOverlayConnected: (connected) => set({ isOverlayConnected: connected }),

  setMuted: (muted, expiresAt = null) => set({
    isMuted: muted,
    muteExpiresAt: expiresAt
  }),

  setAiSpeaking: (speaking) => set({ isAiSpeaking: speaking }),

  setAiThinking: (thinking) => set({ isAiThinking: thinking }),

  setSessionHealth: (health) => set({ sessionHealth: health }),

  addTranscriptEntry: (text, thought, source = 'direct') => set((state) => {
    const now = Date.now();
    const lastEntry = state.transcript[state.transcript.length - 1];
    if (
      lastEntry
      && lastEntry.text === text
      && (lastEntry.thought ?? '') === (thought ?? '')
      && now - lastEntry.timestamp < TRANSCRIPT_DUPLICATE_WINDOW_MS
    ) {
      return { transcript: state.transcript };
    }

    const nextEntry = {
      id: `${now}-${Math.random().toString(36).substr(2, 9)}`,
      text,
      thought,
      source,
      timestamp: now,
    };

    const transcript = state.transcript.length >= MAX_TRANSCRIPT_ENTRIES
      ? [...state.transcript.slice(-(MAX_TRANSCRIPT_ENTRIES - 1)), nextEntry]
      : [...state.transcript, nextEntry];

    return { transcript };
  }),

  clearTranscript: () => set({ transcript: [] }),

  updateStats: (stats) => set((state) => ({ ...state, ...stats })),

  setBackgroundStyle: (style: BackgroundStyle) => {
    localStorage.setItem('sb_bg_style', style);
    set({ backgroundStyle: style });
  },

  reset: () => set(initialState),
}));
